import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, Zap, Gauge, ShieldCheck, Phone } from "lucide-react";
import { PHONE_TEL, PHONE_DISPLAY, IMAGES } from "@/lib/site";

const LINES = ["Engineering", "Digital Growth.", "End to End."];

const badge = "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow-lg";

export const Hero = () => (
  <section id="top" className="relative overflow-hidden technical-grid pt-32 sm:pt-40 pb-20 sm:pb-28">
    <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-primary/15 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-52 -left-40 w-[32rem] h-[32rem] rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center relative">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-6">
          Marketing · Websites · Chatbots · Booking · SEO · Hosting
        </motion.p>
        <h1 className="font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
                           transition={{ duration: 0.7, delay: 0.15 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}>
                {i === 1 ? <span className="text-primary">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }}
                  className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-300">
          Wynova Systems builds the whole engine — the marketing, websites, chatbots, and booking
          systems that win customers, and the hosting that keeps it all fast, secure, and always on.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.85 }}
                    className="mt-10 flex flex-wrap items-center gap-4">
          <a href="#contact" data-testid="hero-primary-cta"
             className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary text-white font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 hover:shadow-blue-700/30 transition-all">
            Start a Project <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <a href="#contact" data-testid="hero-secondary-cta"
             className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-slate-900/80 dark:border-slate-200/80 font-semibold hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 transition-colors">
            <CalendarClock className="w-4 h-4" /> Book a Free Demo
          </a>
          <a href={PHONE_TEL} data-testid="hero-phone-cta"
             className="inline-flex items-center gap-2 px-6 py-4 rounded-full font-semibold border-2 border-slate-300 dark:border-slate-600 hover:border-primary hover:text-primary transition-colors">
            <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
          </a>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }} className="lg:col-span-5 relative">
        <div className="relative rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl shadow-blue-900/20 rotate-1 hover:rotate-0 transition-transform duration-500">
          <img src={IMAGES.hero} alt="Wynova Systems growth visualization" className="w-full h-80 sm:h-96 object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
        </div>
        <div className={`${badge} absolute -top-4 -left-3 animate-float-slow`} data-testid="hero-badge-uptime">
          <ShieldCheck className="w-4 h-4 text-primary" /> 99.99% Uptime SLA
        </div>
        <div className={`${badge} absolute top-1/2 -right-3 animate-float-slow`} style={{ animationDelay: "1.5s" }} data-testid="hero-badge-latency">
          <Gauge className="w-4 h-4 text-cyan-500" /> &lt;40ms Latency
        </div>
        <div className={`${badge} absolute -bottom-4 left-8 animate-float-slow`} style={{ animationDelay: "0.8s" }} data-testid="hero-badge-growth">
          <Zap className="w-4 h-4 text-amber-500" /> 3.4x Conversion Growth
        </div>
      </motion.div>
    </div>
  </section>
);
